/* eslint-disable no-console */
import * as Sentry from '@sentry/react'

type Level = 'debug' | 'info' | 'warn' | 'error'

const enabled: Record<Level, boolean> =
  import.meta.env.DEV
    ? { debug: true, info: true, warn: true, error: true }
    : { debug: false, info: false, warn: true, error: true }

function fmt(scope: string, args: unknown[]): unknown[] {
  return [`[${scope}]`, ...args]
}

/**
 * Extract a JS Error from the logger arg list (the convention is
 * `logger.e('failed to X', err, { context })`). Returns null if no Error
 * instance is found — Sentry will still receive the message via captureMessage.
 */
function findError(args: unknown[]): Error | null {
  for (const a of args) {
    if (a instanceof Error) return a
  }
  return null
}

// PII sanitization — applied to every arg before it reaches Sentry.
// Reason: query keys ({ key: ['preview-tokens', email] } etc.), CRM helper
// callbacks, and free-text booking notes all routinely include emails,
// phones, and player names. Without sanitization those land on the Sentry
// EU ingest as `extra.message` and become a GDPR + data-residency issue.
//
// Strategy: regex-redact email + phone patterns inside strings; replace
// known PII keys inside objects with '<redacted>'. Stops at depth 4 to
// keep the cost bounded for the hot path. We do NOT mutate the original
// args — sanitized copies only flow to Sentry, console gets the originals.
const PII_KEYS = new Set([
  'email',
  'phone',
  'phoneE164',
  'playerEmail',
  'playerPhone',
  'ownerEmail',
  'ownerPhone',
  'buyerName',
  'buyerEmail',
  'customerEmail',
  'customerName',
  'customerPhone',
  'displayName',
  'fullName',
  'name',
])
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g
// Match 9-15 digit sequences (with optional + prefix + spaces/dashes/parens)
// to cover ES local + international numbers without over-matching booking
// IDs (Firestore docIds are 20 chars alphanumeric, not digit-only).
const PHONE_RE = /\+?\d[\d\s().-]{7,14}\d/g

function sanitizeString(s: string): string {
  return s.replace(EMAIL_RE, '<email>').replace(PHONE_RE, '<phone>')
}

function sanitize(value: unknown, depth = 0): unknown {
  if (value == null) return value
  if (depth > 4) return '<deep>'
  if (typeof value === 'string') return sanitizeString(value)
  if (typeof value === 'number' || typeof value === 'boolean') return value
  if (Array.isArray(value)) return value.map((v) => sanitize(v, depth + 1))
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = PII_KEYS.has(k) ? '<redacted>' : sanitize(v, depth + 1)
    }
    return out
  }
  return value
}

function reportToSentry(
  level: 'warning' | 'error',
  scope: string,
  args: unknown[],
): void {
  // No DSN → Sentry.init is a no-op, the capture calls are no-ops too.
  const err = findError(args)
  const sanitized = args.map((a) => (a instanceof Error ? a : sanitize(a)))
  const message = `[${scope}] ${sanitized
    .filter((a) => !(a instanceof Error))
    .map((a) =>
      typeof a === 'string' ? a : (() => {
        try {
          return JSON.stringify(a)
        } catch {
          return String(a)
        }
      })(),
    )
    .join(' ')}`
  if (err) {
    Sentry.captureException(err, {
      level,
      tags: { scope },
      extra: { message },
    })
  } else {
    Sentry.captureMessage(message, level)
  }
}

export interface Logger {
  d: (...args: unknown[]) => void
  i: (...args: unknown[]) => void
  w: (...args: unknown[]) => void
  e: (...args: unknown[]) => void
}

/**
 * Scoped logger. Writes to the console in dev, gated in prod; `w` + `e`
 * additionally report to Sentry so production incidents surface on the
 * dashboard without every call site re-implementing `captureException`.
 */
export function createLogger(scope: string): Logger {
  return {
    d: (...args) => enabled.debug && console.debug(...fmt(scope, args)),
    i: (...args) => enabled.info && console.info(...fmt(scope, args)),
    w: (...args) => {
      if (enabled.warn) console.warn(...fmt(scope, args))
      reportToSentry('warning', scope, args)
    },
    e: (...args) => {
      if (enabled.error) console.error(...fmt(scope, args))
      reportToSentry('error', scope, args)
    },
  }
}

export const logger = createLogger('app')
